// De coachadviezen die een energiecoach bij een huishouden kan registreren,
// afgeleid uit de interventiebibliotheek die de app toch al ophaalt.
//
// GEEN EIGEN LIJST. Een handgeschreven lijst adviezen was de eerste opzet en is
// bewust weggegooid: dezelfde titels stonden dan op twee plekken (hier en in de
// meetstandaard), ze liepen bij elke nieuwe publicatie verder uit elkaar, en de
// kengetallen — die de standaard gewoon publiceert — moesten dan alsnog met de
// hand aan een code worden geknoopt. De bibliotheek IS de lijst; hier staat
// alleen welk deel ervan een coachadvies is.
//
// WAT EEN COACHADVIES IS, staat niet in het document. De bibliotheek kent maar
// een soort regel ('interventie') en geen veld dat product van advies scheidt.
// Die grens is daarom hier gemaakt, langs vier voorwaarden:
//
//   1. domein 'Klimaat & Energie'. Circulariteit en biodiversiteit staan wel in
//      dezelfde bibliotheek, maar een energiecoach adviseert er niet op. Ze zijn
//      uitgesloten, niet vergeten (besluit 2026-08-18).
//   2. Er is een besparing op de energierekening. Dat is wat de coach komt
//      brengen; een interventie die alleen maatschappelijk iets oplevert (of
//      waarvan de standaard nog geen kengetal heeft, zoals EG11 'apparaten op
//      nachttarief') hoort niet in dit lijstje.
//   3. Geen product. Alles wat via materialInterventionDefaults al aan een
//      materiaal hangt, staat al in de materialenlijst; twee keer aanbieden zou
//      hetzelfde effect twee keer laten registreren.
//      Dat geldt ook voor een productvariant: sinds 0.10 hebben tochtband,
//      kaderprofielen, kit en deurdorpels een eigen code (ET03a-d) met het
//      kengetal van ET03. Een code die bestaat uit een materiaalcode plus een
//      kleine letter telt daarom ook als materiaal.
//   4. Geen woningverbetering of opwek. Spouwmuurisolatie, zonnepanelen en een
//      warmtepomp besparen wel op de rekening, maar dat zijn ingrepen aan de
//      woning — meestal een besluit van de verhuurder — en geen advies dat de
//      coach aan tafel geeft en afvinkt.
//
// NIEUWE INTERVENTIES KOMEN VANZELF MEE. Publiceert de standaard er een die aan
// alle vier voldoet, dan staat hij in de keuzelijst zonder dat hier iets hoeft
// te veranderen. Dat is precies de bedoeling.
//
// AI-CONTEXT: See docs/features/materials-co2-circulariteit.md

import { MATERIAL_INTERVENTION_DEFAULTS } from './materialInterventionDefaults'

const ENERGY_DOMAIN = 'Klimaat & Energie'

// Ingrepen aan de woning en opwek: wel een besparing, geen coachadvies.
const BUILDING_MEASURES = new Set(['ISO', 'ISD', 'ISV', 'ISB', 'ZON', 'WP', 'ZDK'])

/** Codes die al als materiaal in de keuzelijst staan. */
const productCodes = () => new Set(
    Object.values(MATERIAL_INTERVENTION_DEFAULTS).map((entry) => entry.code)
)

// 'ET03a' -> 'ET03'. Alleen het patroon letters-cijfers-kleine letter: een
// slug als 'textiel-hergebruiken' is geen variant van iets.
const VARIANT_OF = /^([A-Z]+\d+)[a-z]$/

/**
 * @param {string} code
 * @param {Set<string>} products
 * @returns {boolean}
 */
const isProduct = (code, products) => {
    if (products.has(code)) return true
    const variant = VARIANT_OF.exec(code || '')
    return Boolean(variant) && products.has(variant[1])
}

/**
 * @typedef {import('../meetstandaard/adaptInterventieDocument').AdaptedInterventie} AdaptedInterventie
 *
 * @typedef {object} CoachAdviceGroup
 * @property {string} category - Groepskop, uit `activiteitstype` van de standaard
 * @property {Array<AdaptedInterventie>} items
 */

/**
 * @param {AdaptedInterventie} intervention
 * @param {Set<string>} products
 * @returns {boolean}
 */
const isCoachAdvice = (intervention, products) => {
    if (!intervention || intervention.domein !== ENERGY_DOMAIN) return false
    if (isProduct(intervention.code, products)) return false
    if (BUILDING_MEASURES.has(intervention.code)) return false
    // > 0 en niet alleen 'is een getal': een interventie zonder kengetal heeft
    // hier null staan, en die mag nooit als 'bespaart niets' gelezen worden —
    // hij hoort simpelweg niet in dit lijstje.
    return typeof intervention.privateEurPerUnit === 'number' && intervention.privateEurPerUnit > 0
}

/**
 * De coachadviezen uit een opgehaalde interventiebibliotheek, gegroepeerd op de
 * activiteitstypen die de standaard zelf hanteert ('Energie besparen',
 * 'Apparaat vervangen').
 *
 * Zonder bibliotheek een lege lijst — nooit een verzonnen terugval. De modal
 * zegt in dat geval dat de meetstandaard niet bereikbaar is; een korter lijstje
 * tonen zou lezen als 'meer adviezen bestaan er niet'.
 *
 * @param {{interventies?: Array<AdaptedInterventie>}|null} library
 * @returns {Array<CoachAdviceGroup>}
 */
const coachAdviceGroups = (library) => {
    const interventies = library?.interventies
    if (!Array.isArray(interventies)) return []

    const products = productCodes()
    const byCategory = new Map()

    interventies
        .filter((intervention) => isCoachAdvice(intervention, products))
        .forEach((intervention) => {
            const category = intervention.activiteitstype || 'Overig'
            if (!byCategory.has(category)) byCategory.set(category, [])
            byCategory.get(category).push(intervention)
        })

    return Array.from(byCategory.entries()).map(([category, items]) => ({ category, items }))
}

export { coachAdviceGroups, isCoachAdvice, BUILDING_MEASURES }
